import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Assuming you pass the specific milestone data as the 'item' prop
export default function SleepCoachMilestoneModal({
  isOpen = false,
  setIsOpen,
  item = {}, // The single milestone object (e.g., {RankImage, title, description, unlockDate})
  progress = false, // If true, use progress tracker theme, else use sleep coach theme
}) {
  const [showConfetti, setShowConfetti] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasItem = Object.keys(item).length > 0;

  useEffect(() => {
    if (isOpen && hasItem) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    } else if (isOpen && !hasItem) {
      // Close if opened with no data
      setIsOpen(false);
    }
  }, [isOpen, hasItem, setIsOpen]);

  const confettiPieces = Array.from({ length: 50 }, (_, i) => i);

  // Define color schemes based on module type
  const getModuleColors = () => {
    if (progress) {
      // Progress Tracker Colors (Green/Teal theme)
      return {
        primaryGradient: "from-emerald-600 to-teal-600",
        secondaryGradient: "from-emerald-500 to-teal-500",
        badgeColor: "bg-emerald-400",
        textGradient: "from-emerald-600 to-teal-600",
        shadowStart: "rgba(16, 185, 129, 0.5)", // emerald-500
        shadowEnd: "rgba(5, 150, 105, 0.8)", // emerald-600
        confettiColors: [
          "#10B981", // emerald-500
          "#0D9488", // teal-600
          "#059669", // emerald-600
          "#0F766E", // teal-700
          "#047857", // emerald-700
          "#115E59", // teal-800
        ],
        buttonGradient: "linear-gradient(150deg, #059669 12.89%, #0D9488 85.22%)",
        badgeEmoji: "📈",
      };
    } else {
      // Sleep Coach Colors (Indigo theme)
      return {
        primaryGradient: "from-indigo-600 to-indigo-800",
        secondaryGradient: "from-indigo-500 to-indigo-700",
        badgeColor: "bg-indigo-400",
        textGradient: "from-indigo-600 to-indigo-800",
        shadowStart: "rgba(79, 70, 229, 0.5)",
        shadowEnd: "rgba(79, 70, 229, 0.8)",
        confettiColors: [
          "#45B7D1",
          "#6B7280",
          "#4ECDC4",
          "#4F46E5", // indigo-600
          "#6366F1", // indigo-500
          "#3730A3", // indigo-800
        ],
        buttonGradient: "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)",
        badgeEmoji: "🌙",
      };
    }
  };

  const formatDate = (dateString) => {
    const today = new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    // Fallback label (translated or English if missing)
    const unlockedLabel = `${t("quizGame.Unlocked")} on`;

    // If text already contains "Unlocked on", return today's formatted text
    if (typeof dateString === "string" && dateString.includes("Unlocked on")) {
      return `${unlockedLabel} ${today}`;
    }

    // Normal formatting for actual date inputs
    if (!dateString) return t("quizGame.Unknown date");

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return `${unlockedLabel} ${today}`;

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return `${unlockedLabel} ${today}`;
    }
  };

  if (!isOpen || !hasItem) {
    return null;
  }

  const moduleColors = getModuleColors();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          >
            {/* Confetti Animation with module-specific colors */}
            {showConfetti && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {confettiPieces.map((i) => (
                  <motion.div
                    key={i}
                    className="absolute w-3 h-3 rounded-sm"
                    style={{
                      backgroundColor: moduleColors.confettiColors[i % moduleColors.confettiColors.length],
                      left: `${Math.random() * 100}%`,
                      top: "-10px",
                    }}
                    initial={{ y: -100, rotation: 0, opacity: 1 }}
                    animate={{
                      y: window.innerHeight + 100,
                      rotation: Math.random() * 360,
                      opacity: 0,
                    }}
                    transition={{
                      duration: Math.random() * 6 + 4,
                      delay: Math.random() * 3,
                      ease: "easeOut",
                    }}
                  />
                ))}
              </div>
            )}

            <motion.div
              className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden flex flex-col"
              initial={{ scale: 0.3, opacity: 0, y: 100, rotateX: -15 }}
              animate={{ scale: 1, opacity: 1, y: 0, rotateX: 0 }}
              exit={{ scale: 0.3, opacity: 0, y: 100, rotateX: 15 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 25,
                mass: 0.8,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Module badge */}
              {/* <div className={`absolute top-4 left-4 px-3 py-1 ${moduleColors.badgeColor} bg-opacity-20 backdrop-blur-sm rounded-full z-20`}>
                <span className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                  {moduleColors.badgeEmoji} 
                </span>
              </div> */}

              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors text-gray-600 text-[23px] z-20"
                style={{ background: moduleColors.buttonGradient }}
              >
                ×
              </button>

              {/* Slide Content */}
              <div className="flex-grow h-[400px]">
                <Swiper slidesPerView={1} className="h-full">
                  <SwiperSlide>
                    <SlideContent
                      item={item}
                      formatDate={formatDate}
                      moduleColors={moduleColors}
                      progress={progress}
                    />
                  </SwiperSlide>
                </Swiper>
              </div>

              {/* Button section (optional - uncomment if needed) */}
              {/* <div className="bg-white p-4">
                <motion.button
                  onClick={() => navigate(progress ? "/progress-tracker" : "/sleep-coach-dashboard")}
                  className="w-full px-6 py-3 text-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 font-semibold cursor-pointer"
                  style={{ background: moduleColors.buttonGradient }}
                >
                  {progress 
                    ? t("progressTracker.View Progress Dashboard")
                    : t("sleepCoachPreferences.View Sleep Dashboard")
                  }
                </motion.button>
              </div> */}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------
// Dedicated Slide Content Component
// ---------------------------------------------------------------------

function SlideContent({ item, formatDate, moduleColors, progress }) {
  const { t } = useTranslation();


  // Determine achievement text
  const getAchievementText = () => {
    if (progress) {
      return "Achievement Unlocked";
    } else {
      return "Milestone Achieved";
    }
  };

  return (
    <div className="relative h-full p-8 text-center flex flex-col justify-center">
      <motion.div className="absolute inset-0 bg-white opacity-10" />

      {/* Floating particles with module-specific colors */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 15 }).map((_, i) => (
          <motion.div
            key={i}
            className={`absolute w-2 h-2 ${moduleColors.badgeColor} rounded-full opacity-60`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.6, 1, 0.6],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 6 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 4,
            }}
          />
        ))}
      </div>

      {item.RankImage && (
        <motion.div
          className="relative mx-auto mb-6"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
        >
          <motion.div
            className={`w-24 h-24 bg-gradient-to-br ${moduleColors.primaryGradient} rounded-full flex items-center justify-center shadow-2xl overflow-hidden`}
            animate={{
              boxShadow: [
                `0 0 20px ${moduleColors.shadowStart}`,
                `0 0 40px ${moduleColors.shadowEnd}`,
                `0 0 20px ${moduleColors.shadowStart}`,
              ],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <img
              src={item.RankImage}
              alt={item.title}
              className="w-16 h-16 object-cover rounded-full"
            />
          </motion.div>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <motion.h2
          className={`text-3xl font-bold bg-gradient-to-r px-10 sm:px-0 ${moduleColors.textGradient} bg-clip-text text-transparent mb-2`}
          animate={{ scale: [1, 1.05, 1] }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 1,
          }}
        >
          {getAchievementText()}! <span className="!text-[red]">{moduleColors.badgeEmoji}</span>
        </motion.h2>

        <motion.p
          className="text-xl font-semibold text-gray-800 mb-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          {item.title}
        </motion.p>

        <motion.p
          className="text-sm text-gray-600 mb-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          {formatDate(item.unlockDate)}
        </motion.p>

        <motion.p
          className="text-sm text-gray-700 mb-6 px-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
        >
          {item.description || 
            (progress 
              ? t("progressTracker.Congratulations on your progress!") 
              : t("sleepCoachPreferences.Congratulations on your sleep journey!")
            )}
        </motion.p>
      </motion.div>
    </div>
  );
}